import React, { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { getRenderedFieldByType } from "../../entries/utils/getRenderedFieldByType";
import { Flex } from "../../../components/Flex";
import { AtiraToast } from "../../../utils/AtiraToast";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import { moduleActions } from "../../../redux/module/module.slice";
import isEmpty from "lodash/isEmpty";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { Drawer } from "../../../components/Drawer";
import { UpdateModuleInstanceDto } from "../../../model/module/dto/module-instance/UpdateModuleInstanceDto";
import { BaseModuleInstance } from "../../../model/module/types/module-instance/BaseModuleInstance";
import { moduleSliceSelectors } from "../../../redux/module/module.selector";
import { useLocation, useParams } from "react-router-dom";
import { entryActions } from "../../../redux/entry/entry.slice";
import { PageMeta } from "../../../model/shared/meta/PageMeta";
import { DrawerDefaultsExtra } from "../../tasks/components/DrawerDefaultsExtra";
import { useDrawersContext } from "../../tasks/drawers-context";
import { InputTypes } from "../../../model/form/types/InputTypes.enum";
import { EntriesSearchDropdown } from "../../../components/shared/EntriesSearchDropdown";
import { Button } from "../../../components/Button";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import styled from "styled-components";
import { Spacing } from "../../../theme/Spacing";
import { CreateEntryDto } from "../../../model/entry/dto/CreateNewEntryDto";
import { Info } from "../../../components/Info";
import { Text } from "../../../components/Text";
import { isValidDate, standardDate } from "../../../utils/Date";
import { formSliceSelectors } from "../../../redux/forms/form.selector";
import { getPossibleExtraKeysForModuleInstances } from "../module-definition/utils/moduleUtils";

const NewEntryButton = styled(Button)`
  padding: ${Spacing.s} ${Spacing.m};
  margin: 0;
  background-color: transparent;
  color: ${({ theme }) => theme.main};
  height: 2.3rem;
  flex-shrink: 0;
  border: ${({ theme }) => `1px solid ${theme.main}`};

  &:hover {
    background-color: ${({ theme }) => theme.main};
    color: ${({ theme }) => theme.white};
  }
`;

const StyledList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
`;

type DynamicModuleInstanceUpdateDrawerProps = {
  open: boolean;
  onClose: VoidFunction;
  instance?: BaseModuleInstance | null;
};

export const DynamicModuleInstanceUpdateDrawer: React.FC<
  DynamicModuleInstanceUpdateDrawerProps
> = ({ open = false, onClose, instance }) => {
  const [loading, setLoading] = useState(false);
  const [createEntryDto, setCreateEntryDto] = useState<CreateEntryDto | null>(
    null,
  );

  const dispatch = useAppDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const { setEntry, setEntryCreateVisible, setEntryCreateAction } =
    useDrawersContext();
  const { control, setError, reset, getValues, handleSubmit, setValue } =
    useForm<UpdateModuleInstanceDto>();
  const { id } = useParams();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const instancesPage = useAppSelector(
    moduleSliceSelectors.selectInstancesPage,
  );
  const instancesPageSize = useAppSelector(
    moduleSliceSelectors.selectInstancesPageSize,
  );
  const parentModuleDefinition = useAppSelector((s) =>
    moduleSliceSelectors.selectModuleDefinitionById(
      s,
      instance?.parentId || "",
    ),
  );
  const forms = useAppSelector(formSliceSelectors.selectForms);
  const instances = useAppSelector(moduleSliceSelectors.selectInstances);
  const moduleDefinition = useAppSelector((s) =>
    moduleSliceSelectors.selectModuleDefinitionById(s, id!),
  );

  const createEntryForm = forms?.find((f) => f._id === createEntryDto?.formId);

  const createEntryFormTitle = createEntryForm?.orphan
    ? t("forms.manual_entry")
    : createEntryForm?.title;

  const isThereCreateEntryDto = !isEmpty(createEntryDto);
  const extraKeys = useMemo(
    () => getPossibleExtraKeysForModuleInstances(moduleDefinition, instances)(),
    [instances, moduleDefinition],
  );

  const watchedValues = useWatch({
    control,
    name: ["fields"],
  });
  const [fields] = watchedValues;

  const requiredInputs = useMemo(() => {
    return parentModuleDefinition?.inputs?.filter(
      (input) => input?.settings?.required === true,
    );
  }, [parentModuleDefinition?.inputs]);

  const isDisabled = useMemo(() => {
    return requiredInputs?.some((input) => {
      if (input.type.toLowerCase() === InputTypes.DATE.toLowerCase()) {
        return !fields?.[input.name];
      }

      return !String(fields?.[input.name])?.length;
    });
  }, [fields, requiredInputs]);

  const renderedInputs = useMemo(() => {
    const trueInputs = parentModuleDefinition?.inputs?.map((input) => (
      <Controller
        key={input._id}
        name={`fields.${input.name}`}
        control={control}
        rules={{
          required: !!input?.settings?.required,
          maxLength: {
            value: input?.settings?.maxChar || Lengths.DESCRIPTION,
            message: t("error.length.less", {
              length: input?.settings?.maxChar || Lengths.DESCRIPTION,
            }),
          },
        }}
        render={({ field, fieldState }) =>
          getRenderedFieldByType(
            input,
            field,
            fieldState,
            input.label,
            undefined,
            {
              // AtiraDatePickerFlat indexes possibleBookedSlots by input
              // name itself - pass the full record (not already indexed by
              // input.name), matching FixedModuleInstanceUpdateDrawer.
              possibleBookedSlots: parentModuleDefinition?._possibleBookedSlots,
            },
          )
        }
      />
    ));

    const possibleExtraInputs = extraKeys
      ?.filter(
        (key) =>
          instance?.fields?.[key] !== undefined &&
          !parentModuleDefinition?.inputs?.some((input) => input.name === key),
      )
      .map((key) => (
        <Controller
          key={key}
          name={`fields.${key}`}
          control={control}
          render={({ field, fieldState }) =>
            getRenderedFieldByType(
              {
                name: key,
                type: InputTypes.TEXT,
                label: t(`common.uncategorized`),
              },
              field,
              fieldState,
              t(`common.uncategorized`),
            )
          }
        />
      ));

    return trueInputs?.concat(possibleExtraInputs || []) || [];
  }, [parentModuleDefinition?.inputs, extraKeys, control, t, instance?.fields]);

  const localOnClose = () => {
    setEntryCreateAction(null);
    setCreateEntryDto(null);
    onClose();
    reset();
  };

  const onCreateNewEntry = () => {
    setEntryCreateVisible(true);

    setEntryCreateAction(() => (dto: CreateEntryDto) => {
      setCreateEntryDto(dto);
      setEntryCreateVisible(false);
    });
  };

  const onSave = async () => {
    try {
      setLoading(true);

      if (isEmpty(instance)) {
        return;
      }

      const dto = getValues();

      if (!dto?.entryId || isThereCreateEntryDto) {
        delete dto?.entryId;
      }

      await dispatch(
        moduleActions.updateModuleInstance({
          ...dto,
          ...(isThereCreateEntryDto ? { entryDto: createEntryDto } : {}),
        } as UpdateModuleInstanceDto),
      ).unwrap();
      if (location.pathname.includes("/entries")) {
        const updatedEntry = await dispatch(
          entryActions.getEntryById({
            entryId: instance?.entryId!,
            userId: user?._id!,
          }),
        ).unwrap();

        setEntry?.(updatedEntry);
      } else {
        await dispatch(
          moduleActions.getModuleInstances({
            userId: user?._id!,
            moduleDefinitionId: instance.parentId,
            keyword: "",
            meta: PageMeta.create({
              page: instancesPage - 1,
              count: instancesPageSize,
            }),
          }),
        ).unwrap();
      }

      localOnClose();

      AtiraToast.success(t("modules.instance.update.success"));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && instance) {
      reset({
        userId: user?._id,
        fields: instance?.fields || {},
        moduleInstanceId: instance._id,
        moduleDefinitionId: instance.parentId,
        entryId: instance?.entryId,
      });

      dispatch(moduleActions.getAllModuleDefinitions({ userId: user?._id! }));
    }
  }, [open, instance, user?._id, reset, dispatch]);

  if (isEmpty(instance) || !parentModuleDefinition) {
    return null;
  }

  return (
    <Drawer
      open={open}
      onClose={localOnClose}
      title={t("modules.instance.update.title", {
        moduleDefinition: parentModuleDefinition.name,
      })}
      maskClosable={false}
      extra={
        <DrawerDefaultsExtra
          onSubmit={handleSubmit(onSave)}
          onCancel={localOnClose}
          config={{
            loading,
            editMode: true,
            disabled: isDisabled,
          }}
        />
      }
      size="large"
      key={`${parentModuleDefinition._id}-${instance._id}`}
    >
      <Flex width={"100%"} flexDirection="column" gap="m">
        {!isThereCreateEntryDto && !instance?.entryId ? (
          <Flex
            gap="s"
            width={"100%"}
            alignItems="center"
            flexDirection="column"
          >
            <EntriesSearchDropdown
              setValue={setValue}
              required={false}
              isSearchVisible={open}
            />

            <NewEntryButton icon={faPlus} onClick={onCreateNewEntry}>
              {t("modules.instance.update.createEntry.button")}
            </NewEntryButton>
          </Flex>
        ) : null}

        {isThereCreateEntryDto ? (
          <Info
            info={
              <Flex flexDirection="column" gap="s">
                <Text color="main">
                  <Trans
                    i18nKey={"modules.instance.update.createEntry.info"}
                    values={{ formTitle: createEntryFormTitle }}
                    components={[<strong />]}
                  />
                </Text>

                <StyledList>
                  {Object.entries(createEntryDto)
                    .filter(
                      ([key, value]) =>
                        !isEmpty(value) &&
                        !["formId", "ownerId", "origin"].includes(key),
                    )
                    .map(([key, value]) => {
                      const fieldLabel = !createEntryForm?.orphan
                        ? createEntryForm?.inputs?.find(
                            (input) => input.name === key,
                          )?.label
                        : t(`common.${key}`);

                      return (
                        <li key={key}>
                          <Text color="main">
                            <strong>{fieldLabel}: </strong>
                            <span>
                              {isValidDate(value)
                                ? standardDate<string>(value as Date, user)
                                : (value as string)}
                            </span>
                          </Text>
                        </li>
                      );
                    })}
                </StyledList>
              </Flex>
            }
          />
        ) : null}

        {renderedInputs}
      </Flex>
    </Drawer>
  );
};
